
export enum AppState {
  INITIALIZING = 'INITIALIZING',
  UPLOAD = 'UPLOAD',
  MODE_SELECTION = 'MODE_SELECTION', // New: Choose between Visual Staging or Editable CAD
  EDITOR = 'EDITOR',
  PROCESSING = 'PROCESSING',
  RESULT = 'RESULT',
  ERROR = 'ERROR'
}

// Generation mode for floor plans
export type GenerationMode = 'visual' | 'vector';

// Vector floor plan data structure (for CAD mode)
export interface VectorWall {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  thickness: number;
}

export interface VectorLabel {
  id: string;
  x: number;
  y: number;
  text: string;
  fontSize?: number;
}

export interface VectorFloorPlan {
  walls: VectorWall[];
  labels: VectorLabel[];
  width: number;
  height: number;
}

export interface ImageState {
  originalFile: File | null;
  originalUrl: string | null;
  processedUrl: string | null;
  maskDataUrl: string | null; // For debugging or history if needed
}

export interface DeclutterOptions {
  prompt: string;
  brushSize: number;
}

export interface WatermarkSettings {
  imageUrl: string;
  x: number; // Normalized 0-1
  y: number; // Normalized 0-1
  scale: number; // Normalized scale relative to image width
  opacity: number; // 0-1
}

export interface UserProfile {
  id: string;
  email: string;
  credits: number;
  subscription_plan?: string;
  stripe_customer_id?: string;
  referral_code?: string;
  /** 'prepaid' (credits) or 'monthly_invoice' (billed monthly, no credits charged). */
  billing_mode?: 'prepaid' | 'monthly_invoice';
  /** Company/account name used on the monthly invoice. */
  invoice_company?: string | null;
  /** Where the monthly statement/invoice is sent. */
  invoice_email?: string | null;
  /** Optional negotiated flat per-plan rate in pence; null = use volume pricing. */
  custom_plan_rate_pence?: number | null;
}

export interface ReferralReward {
  id: string;
  bonus_credits: number;
  referrer_bonus_credits?: number;
  purchased_credits: number;
  created_at: string;
  referred_email: string;
  referred_name?: string;
  reward_kind?: string;
  status?: string;
}

export interface ReferralStats {
  success: boolean;
  referral_code: string;
  signup_count: number;
  friends_joined?: number;
  total_bonus_credits: number;
  credits_earned?: number;
  invite_base_url?: string;
  rewards: ReferralReward[];
}

export interface GeneratedImage {
  id: string;
  user_id: string;
  input_url: string;
  output_url: string;
  prompt?: string;
  status: string;
  created_at: string;
  type?: string; // Added to match new database schema
  service_type?: 'photoclear' | 'staging'; // Identifies which app created the image
}

export interface CreditTransaction {
  id: string;
  user_id: string;
  amount: number;
  type: string;
  description: string;
  created_at: string;
}

export interface Price {
  id: string;
  product_id: string;
  active: boolean;
  currency: string;
  type: 'one_time' | 'recurring';
  unit_amount: number;
  interval?: string;
  interval_count?: number;
}

export interface Product {
  id: string;
  active: boolean;
  name: string;
  description?: string;
  image?: string;
  metadata?: {
    credits?: string; // lowercase
    bonus?: string;
    popular?: string;
    [key: string]: any;
  };
  prices?: Price[];
}

export interface AdminUser {
  user_id: string;
  role: 'admin' | 'superadmin' | 'editor';
  created_at: string;
}

export interface PromoCode {
  id: string;
  code: string;
  credits: number;
  max_uses: number | null;
  current_uses: number;
  expires_at: string | null;
  active: boolean;
  created_by: string | null;
  description: string | null;
  created_at: string;
}

export interface PromoRedemption {
  id: string;
  promo_code_id: string | null;
  code: string;
  user_id: string;
  credits_granted: number;
  redeemed_at: string;
}

export interface AdminAction {
  id: string;
  admin_id: string | null;
  action_type: 'grant_credits' | 'create_promo' | 'update_promo' | 'deactivate_promo';
  target_user_id: string | null;
  details: any;
  created_at: string;
}

export interface RedeemPromoResult {
  success: boolean;
  error?: 'NOT_AUTHENTICATED' | 'INVALID_CODE' | 'CODE_INACTIVE' | 'CODE_EXPIRED' | 'CODE_EXHAUSTED' | 'ALREADY_REDEEMED';
  credits_added?: number;
  code?: string;
}

// =========================================
// BEHAVIOR MONITORING TYPES
// =========================================

export type EventType = 
  | 'magic_link_requested'
  | 'login'
  | 'generation_started'
  | 'generation_completed'
  | 'generation_failed'
  | 'credits_granted'
  | 'credits_used'
  | 'payment_completed'
  | 'sketchplan_guest_order_started'
  | 'sketchplan_guest_service_selected'
  | 'sketchplan_guest_upload_completed'
  | 'sketchplan_guest_checkout_started'
  | 'sketchplan_guest_payment_completed'
  | 'sketchplan_post_purchase_signup_started'
  | 'sketchplan_guest_order_claimed'
  | 'sketchplan_client_login_clicked'
  | 'pro_order_card_checkout'
  | 'pro_order_submitted'
  | 'promo_redeemed'
  | 'user_status_changed'
  | 'ip_control_changed'
  | 'domain_control_changed';

export type UserRiskStatus = 'ok' | 'watch' | 'restricted' | 'banned';

export type ControlStatus = 'ok' | 'throttled' | 'blocked';

export type DomainControlStatus = 'ok' | 'throttled' | 'require_payment' | 'blocked';

export interface EventLog {
  id: string;
  created_at: string;
  event_type: EventType;
  user_id: string | null;
  user_email: string | null;
  ip_address: string | null;
  user_agent: string | null;
  metadata: Record<string, any>;
}

export interface UserWithStatus {
  id: string;
  email: string;
  credits: number;
  created_at: string;
  risk_status: UserRiskStatus;
  status_reason: string | null;
  status_updated_at: string | null;
  event_count: number;
  last_activity: string | null;
}

export interface UserTimeline {
  user: {
    id: string;
    email: string;
    credits: number;
    created_at: string;
    risk_status: UserRiskStatus;
    status_reason: string | null;
    status_updated_at: string | null;
  };
  events: EventLog[];
}

export interface IPAnalytics {
  ip_address: string;
  event_count: number;
  user_count: number;
  email_count: number;
  emails?: string[];
  last_activity: string;
  first_activity: string;
  control_status: ControlStatus;
  throttle_multiplier: number;
  control_reason: string | null;
}

export interface DomainAnalytics {
  domain: string;
  user_count: number;
  event_count: number;
  last_activity: string;
  control_status: DomainControlStatus;
  throttle_multiplier: number;
  control_reason: string | null;
}

export interface ActivityFeedFilters {
  eventType?: EventType;
  userId?: string;
  ipAddress?: string;
  limit?: number;
  offset?: number;
}

export interface RateLimitCheck {
  allowed: boolean;
  reason?: 'domain_blocked' | 'ip_blocked' | 'rate_limited';
  message?: string;
  require_payment?: boolean;
}

// =========================================
// GENERATIONS & CREDIT LEDGER TYPES
// =========================================

export type GenerationStatus = 'queued' | 'started' | 'completed' | 'failed';

export interface Generation {
  id: string;
  created_at: string;
  user_id: string | null;
  user_email: string | null;
  status: GenerationStatus;
  credits_used: number;
  input_asset_path: string | null;
  output_image_path: string | null;
  ip_address: string | null;
  metadata: Record<string, any>;
  completed_at: string | null;
  error_message: string | null;
}

export type CreditRefType = 'generation' | 'purchase' | 'promo' | 'admin_grant' | 'refund' | 'welcome_bonus';

export interface CreditLedgerEntry {
  id: string;
  created_at: string;
  user_id: string;
  delta: number;
  reason: string;
  ref_type: CreditRefType | null;
  ref_id: string | null;
  created_by: string | null;
  metadata: Record<string, any>;
}

export interface GenerationsFilters {
  userId?: string;
  userEmail?: string;
  status?: GenerationStatus;
  dateFrom?: string;
  dateTo?: string;
  limit?: number;
  offset?: number;
}

export interface UserWithStatusExtended extends UserWithStatus {
  credit_balance: number;
  generation_count: number;
}

// =========================================
// SALES METRICS TYPES
// =========================================

export interface SalesMetrics {
  gross_revenue_usd: number;
  revenue_gbp?: number;
  revenue_usd?: number;
  revenue_eur?: number;
  sales_with_amount?: number;
  sales_without_amount?: number;
  credits_granted?: number;
  credits_sold: number;
  credits_used: number;
  credits_refunded: number;
  total_cost_usd: number;
  gross_margin_usd: number;
  gross_margin_pct: number;
  completed_generations: number;
  failed_generations: number;
  total_generations: number;
  failed_rate_pct: number;
  avg_cost_per_generation: number;
  avg_margin_per_generation: number;
  credit_price_usd: number;
}

export type SalesPeriod = '7d' | '30d' | 'all';

// =========================================
// FLOOR PLAN EDITOR TYPES
// =========================================

/**
 * Amendment types for floor plan editing
 * Used by both EditorRightSidebar and canvasDrawing service
 */
export type AmendmentType = 
  | 'addDoorLeft'
  | 'addDoorRight'
  | 'addWindow' 
  | 'removeWall' 
  | 'buildWall' 
  | 'flipSwing' 
  | 'clearObject'
  | 'addToilet'
  | 'addSink'
  | 'addBathtub'
  | 'addShower'
  | 'addStove'
  | 'addKitchenSink'
  | 'addText';

/**
 * Selection box for floor plan editing
 * Uses normalized (0-1) coordinates relative to image dimensions
 */
export interface EditorSelectionBox {
  /** X position relative to natural image width (0-1 normalized) */
  x: number;
  /** Y position relative to natural image height (0-1 normalized) */
  y: number;
  /** Width relative to natural image width (0-1 normalized) */
  width: number;
  /** Height relative to natural image height (0-1 normalized) */
  height: number;
}

/**
 * Amendment action configuration for sidebar buttons
 */
export interface AmendmentAction {
  type: AmendmentType;
  prompt: string;
  icon: React.ElementType;
  label: string;
  color: 'blue' | 'green' | 'amber' | 'red';
}

/**
 * Selector tool type for the editor
 */
export type SelectorTool = 'box' | 'brush' | null;

/**
 * Result from a canvas drawing operation
 */
export interface DrawingResult {
  success: boolean;
  imageDataUrl: string;
  error?: string;
}

/**
 * Text label overlay for floor plan editing
 * Position is normalized (0-1) relative to image dimensions
 */
export interface TextLabel {
  id: string;
  /** Text content (supports multi-line with \n) */
  text: string;
  /** X position (0-1 normalized) */
  x: number;
  /** Y position (0-1 normalized) */
  y: number;
  /** Font size in pixels */
  fontSize: number;
  /** Font color */
  color: string;
  /** Whether the label is currently being edited */
  isEditing?: boolean;
}

// =========================================
// NORTH POINT TYPES
// =========================================

/**
 * North point symbol style options
 */
export type NorthPointStyle = 
  | 'compass-rose'
  | 'n-arrow'
  | 'hollow-arrow'
  | 'circle-arrow'
  | 'triangle'
  | 'double-line';

/**
 * North point symbol placement and configuration
 * Position is normalized (0-1) relative to plan bounding box
 */
export interface NorthPoint {
  id: string;
  /** Symbol style */
  style: NorthPointStyle;
  /** X position relative to plan bbox (0-1 normalized) */
  x: number;
  /** Y position relative to plan bbox (0-1 normalized) */
  y: number;
  /** Rotation in degrees (rotates around center) */
  rotation: number;
  /** Size multiplier (default 1.0) */
  scale: number;
}

// =========================================
// CALIBRATION & AREA CALCULATION TYPES
// =========================================

/**
 * Confidence level for area calculations
 */
export type AreaConfidence = 'high' | 'medium' | 'low';

/**
 * Enhanced calibration for scale and area calculations
 */
export interface Calibration {
  /** Pixels per meter (canonical unit for all calculations) */
  pxPerMetre: number;
  /** Reference line ID that was used to set the scale */
  referenceLineId?: string;
  /** Confidence level based on how scale was set */
  confidence: AreaConfidence;
}

/**
 * Room area calculation result
 */
export interface RoomArea {
  /** Unique room identifier */
  roomId: string;
  /** Room label/name */
  label: string;
  /** Polygon points defining the room boundary */
  polygonPoints: { x: number; y: number }[];
  /** Area in square pixels */
  areaPx2: number;
  /** Area in square meters (only if scale exists) */
  areaM2: number | null;
  /** Area in square feet (only if scale exists) */
  areaFt2: number | null;
  /** Whether the room polygon is fully closed */
  isClosed: boolean;
}

/**
 * Total area summary
 */
export interface AreaSummary {
  /** Total area in square meters */
  totalM2: number | null;
  /** Total area in square feet */
  totalFt2: number | null;
  /** Individual room areas */
  rooms: RoomArea[];
  /** Overall confidence level */
  confidence: AreaConfidence;
  /** Whether scale has been set */
  hasScale: boolean;
  /** Percentage of rooms that are closed polygons */
  closedRoomPercentage: number;
}

// =========================================
// EXPORT & DISCLAIMER TYPES
// =========================================

/**
 * Export configuration for floor plan downloads
 */
export interface ExportConfig {
  /** Padding around the floor plan in pixels */
  padding: number;
  /** Whether to include disclaimer footer */
  includeFooter: boolean;
  /** Footer height in pixels (calculated from text) */
  footerHeight: number;
  /** Image format */
  format: 'png' | 'jpeg';
  /** JPEG quality (0-1) */
  quality: number;
  /** Resolution multiplier (1 = original, 2 = 2x) */
  scale: number;
}

/**
 * Disclaimer footer settings
 */
export interface DisclaimerSettings {
  /** Main disclaimer text (supports {totalArea} placeholder) */
  disclaimerText: string;
  /** Optional property name/address */
  propertyName?: string;
  /** Whether to show generation date */
  showDate: boolean;
  /** Whether to show branding */
  showBranding: boolean;
  /** Custom branding text or company name */
  brandingText?: string;
}

/**
 * Default disclaimer template
 */
export const DEFAULT_DISCLAIMER_TEXT = `APPROX. GROSS INTERNAL FLOOR AREA {totalArea}

This floor plan is for illustrative purposes only. All measurements are approximate and should not be relied upon. Please verify dimensions on site.`;

/**
 * Default export configuration
 */
export const DEFAULT_EXPORT_CONFIG: ExportConfig = {
  padding: 40,
  includeFooter: true,
  footerHeight: 100,
  format: 'png',
  quality: 0.95,
  scale: 2
};

/**
 * Default disclaimer settings
 */
export const DEFAULT_DISCLAIMER_SETTINGS: DisclaimerSettings = {
  disclaimerText: DEFAULT_DISCLAIMER_TEXT,
  showDate: true,
  showBranding: false
};

// =========================================
// BRANDING PROFILE TYPES
// =========================================

/**
 * Watermark position preset on the output image
 */
export type WatermarkPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';

/**
 * Complete branding profile for an agency/user.
 * Persisted to localStorage and applied across exports.
 */
export interface BrandingProfile {
  /** Agency / company name shown in export footer and watermark */
  agencyName: string;
  /** Optional tagline shown beneath agency name in footer */
  tagline?: string;
  /** Base64 data URL of the agency logo */
  logoDataUrl?: string;
  /**
   * Base64 data URL of a reference floor plan whose visual style the AI should
   * copy when converting sketches. Overrides the app's built-in reference.
   */
  styleReferenceDataUrl?: string;

  // ---- Colours ----
  /** Primary brand colour (hex) – used for wall lines, footer accents */
  primaryColor: string;
  /** Accent / secondary colour (hex) – used for highlights */
  accentColor: string;
  /** Footer background colour (hex) */
  footerBgColor: string;
  /** Footer text colour (hex) */
  footerTextColor: string;

  // ---- Typography ----
  /** CSS font-family string applied to plan labels and footer text */
  fontFamily: string;

  // ---- Watermark ----
  /** Whether to overlay the logo watermark on raster image exports */
  watermarkEnabled: boolean;
  /** Corner / centre position preset for the watermark */
  watermarkPosition: WatermarkPosition;
  /** Opacity of the watermark (0–1) */
  watermarkOpacity: number;
  /** Scale of the watermark relative to the image width (0–1) */
  watermarkScale: number;

  // ---- Floor plan canvas ----
  /** Whether to apply brand primary colour to wall lines */
  useColoredWalls: boolean;
  /** Whether to apply brand font to plan labels */
  useBrandFont: boolean;
  /** Whether to show agency logo in the export footer */
  showLogoInFooter: boolean;
}

/** Position presets with human-readable labels */
export const WATERMARK_POSITIONS: { value: WatermarkPosition; label: string }[] = [
  { value: 'top-left',     label: 'Top Left' },
  { value: 'top-right',    label: 'Top Right' },
  { value: 'bottom-left',  label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
  { value: 'center',       label: 'Centre' },
];

/** Curated professional font families available in the app */
export const BRAND_FONTS: { value: string; label: string; category: string }[] = [
  { value: 'Arial',            label: 'Arial',            category: 'System' },
  { value: 'Georgia',          label: 'Georgia',          category: 'System' },
  { value: 'Helvetica',        label: 'Helvetica',        category: 'System' },
  { value: "'Times New Roman'", label: 'Times New Roman', category: 'System' },
  { value: 'Montserrat',       label: 'Montserrat',       category: 'Google' },
  { value: 'Raleway',          label: 'Raleway',          category: 'Google' },
  { value: 'Playfair Display', label: 'Playfair Display', category: 'Google' },
  { value: 'Lato',             label: 'Lato',             category: 'Google' },
  { value: 'Roboto',           label: 'Roboto',           category: 'Google' },
  { value: 'Open Sans',        label: 'Open Sans',        category: 'Google' },
  { value: 'Nunito',           label: 'Nunito',           category: 'Google' },
  { value: 'Cormorant Garamond', label: 'Cormorant Garamond', category: 'Google' },
];

/** Brand colour palette presets */
export const BRAND_COLOR_PRESETS: { name: string; primary: string; accent: string; footerBg: string; footerText: string }[] = [
  { name: 'Midnight Blue',  primary: '#1e3a5f', accent: '#3b82f6', footerBg: '#1e3a5f', footerText: '#ffffff' },
  { name: 'Forest Green',   primary: '#14532d', accent: '#22c55e', footerBg: '#14532d', footerText: '#ffffff' },
  { name: 'Charcoal',       primary: '#1c1c1c', accent: '#9ca3af', footerBg: '#1c1c1c', footerText: '#ffffff' },
  { name: 'Terracotta',     primary: '#7c2d12', accent: '#f97316', footerBg: '#7c2d12', footerText: '#ffffff' },
  { name: 'Slate',          primary: '#1e293b', accent: '#64748b', footerBg: '#f8fafc', footerText: '#1e293b' },
  { name: 'Classic White',  primary: '#374151', accent: '#6b7280', footerBg: '#ffffff', footerText: '#374151' },
  { name: 'Navy & Gold',    primary: '#1e3a5f', accent: '#d4a017', footerBg: '#1e3a5f', footerText: '#d4a017' },
  { name: 'Luxury Purple',  primary: '#4c1d95', accent: '#c4b5fd', footerBg: '#4c1d95', footerText: '#ffffff' },
];

export const DEFAULT_BRANDING_PROFILE: BrandingProfile = {
  agencyName: '',
  primaryColor: '#1e3a5f',
  accentColor: '#3b82f6',
  footerBgColor: '#ffffff',
  footerTextColor: '#374151',
  fontFamily: 'Arial',
  watermarkEnabled: false,
  watermarkPosition: 'bottom-right',
  watermarkOpacity: 0.7,
  watermarkScale: 0.18,
  useColoredWalls: false,
  useBrandFont: false,
  showLogoInFooter: false,
};

// PhotoPlan types have moved to apps/photoplan/types.ts (photoplan.rehub.studio)
